import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import Breadcrumb from "../../../../component/breadcrumb";
import OrderLifecycleTimeline from "../../../../component/orderLifecycle/OrderLifecycleTimeline";
import CustomPopupForm from "../../../../component/customPopupModel";
import CustomButton from "../../../../component/elements/CustomButton/CustomButton";
import axiosInstance from "../../../../configuration/request";
import { useSnackbar } from "../../../../component/SnackbarContext";
import { dateFormat } from "../../../../utils/dateFormat";
import { formatServiceType } from "../../../../utils/customerHealperFunctions";
import { getOrderHolder } from "../../../../constants/orderLifecycle";
import {
  getPartnerFromPickups,
  mapOrderToViewDetails,
  resolveDeliveryAddress,
  resolveDeliverySchedule,
  resolvePickupAddress,
  resolvePickupSchedule,
} from "../../../../utils/orderTrackingHelpers";

const BCrumb = [
  { to: "/app/order-tracking", title: "Order Tracking" },
  { title: "Order Lifecycle" },
];

function InfoCard({ label, value }) {
  return (
    <div className="bg-white rounded-xl border border-slate-100 px-4 py-3">
      <p className="text-xs text-slate-500">{label}</p>
      <p className="text-sm font-semibold text-slate-900 mt-1 break-words">
        {value || "—"}
      </p>
    </div>
  );
}

function AddressCard({ title, address }) {
  return (
    <div className="bg-white rounded-xl border border-slate-100 px-4 py-4">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
        {title}
      </p>
      <p className="text-sm font-semibold text-slate-900 mt-2 break-words leading-relaxed">
        {address}
      </p>
    </div>
  );
}

function PartnerCard({ title, partner }) {
  return (
    <div className="bg-white rounded-xl border border-slate-100 px-4 py-3">
      <p className="text-xs text-slate-500">{title}</p>
      <p className="text-sm font-semibold text-slate-900 mt-1">
        {partner.assigned ? partner.name : "Not assigned"}
      </p>
      <span
        className={`inline-block mt-2 text-xs font-medium px-2 py-0.5 rounded-full ${
          partner.assigned
            ? "bg-emerald-100 text-emerald-700"
            : "bg-amber-100 text-amber-700"
        }`}
      >
        {partner.assigned ? "Assigned" : "Unassigned"}
      </span>
    </div>
  );
}

export default function OrderTrackingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const showSnackbar = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(null);
  const [orderViewOpen, setOrderViewOpen] = useState(false);

  useEffect(() => {
    if (!id) return;

    setLoading(true);
    axiosInstance
      .get(`/customer/orders?order_id=${id}`)
      .then((res) => {
        const data = res?.data?.data?.[0];
        if (!data) {
          showSnackbar("Order not found", "error");
          navigate("/app/order-tracking", { replace: true });
          return;
        }
        setOrder(data);
      })
      .catch((error) => {
        showSnackbar(
          error?.response?.data?.message || "Failed to fetch order details",
          "error"
        );
        navigate("/app/order-tracking", { replace: true });
      })
      .finally(() => setLoading(false));
  }, [id, navigate, showSnackbar]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[320px]">
        <CircularProgress size={36} />
      </div>
    );
  }

  if (!order) return null;

  const pickupPartner = getPartnerFromPickups(order.pickups, "pickup");
  const deliveryPartner = getPartnerFromPickups(order.pickups, "delivery");
  const pickupAddress = resolvePickupAddress(order);
  const deliveryAddress = resolveDeliveryAddress(order);
  const orderViewDetails = mapOrderToViewDetails(order);

  return (
    <div className="px-4 md:px-6 py-4">
      <Breadcrumb title="Order Lifecycle" items={BCrumb} />

      <div className="mt-4 bg-white rounded-2xl border border-slate-100 shadow-sm p-5 md:p-6">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">
              Order {order.order_id}
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              Placed on {dateFormat(order.created_at) || "—"}
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <div className="inline-flex items-center gap-2 bg-sky-50 border border-sky-100 rounded-full px-4 py-2">
              <span className="text-xs text-sky-600 font-medium">Status</span>
              <span className="text-sm font-semibold text-sky-800">
                {order.status}
              </span>
            </div>
            <CustomButton
              onClick={() => setOrderViewOpen(true)}
              customeClass="customer-dark-button h-[42px] px-5 whitespace-nowrap"
            >
              <VisibilityOutlinedIcon className="mr-2" fontSize="small" />
              View Order
            </CustomButton>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          <InfoCard label="Customer" value={order?.user?.name} />
          <InfoCard label="Store" value={order?.store?.storeName} />
          <InfoCard
            label="Service"
            value={formatServiceType(order?.services?.service_type)}
          />
          <InfoCard label="Order With" value={getOrderHolder(order.status)} />
          <InfoCard
            label="Pickup Schedule"
            value={resolvePickupSchedule(order)}
          />
          <InfoCard
            label="Delivery Schedule"
            value={resolveDeliverySchedule(order)}
          />
          <InfoCard label="Contact" value={order?.user?.phone_number} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
          <AddressCard title="Pickup Address" address={pickupAddress} />
          <AddressCard title="Delivery Address" address={deliveryAddress} />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
          <PartnerCard title="Pickup Partner" partner={pickupPartner} />
          <PartnerCard title="Delivery Partner" partner={deliveryPartner} />
        </div>

        <div>
          <h3 className="text-base font-semibold text-slate-900 mb-4">
            Order Lifecycle
          </h3>
          <OrderLifecycleTimeline status={order.status} variant="vertical" />
        </div>
      </div>

      <CustomPopupForm
        isOpen={orderViewOpen}
        handleClose={() => setOrderViewOpen(false)}
        dialogType="order-view"
        dialogDetails={orderViewDetails}
        onSubmit={() => setOrderViewOpen(false)}
        showMismatchReport={false}
      />
    </div>
  );
}
